// Adapt a MerchandisingVariation (returned by GET /retailers/{id}/variations)
// to the shape the variation list UI expects.
//
// The document resolves translatable fields (variation name, option
// names/values) in the organization's default locale, as single strings:
// they are wrapped in one-entry translations arrays, which getTranslation()
// resolves through its last-resort branch. Fields not carried by the
// document are filled with safe placeholders (null / false) — same approach
// as mapMerchandisingProduct.

const toTranslations = (translated) =>
    (translated || []).map((t) => ({locale: t.locale, name: t.translated}));

// See mapMerchandisingProduct: one frozen identity for "no error".
const EMPTY_VALIDATION_ERRORS = Object.freeze([]);

export function mapMerchandisingVariationToVariation(esVariation) {
    return {
        // Mass actions (massUpdate) collect the selected rows through item["@id"].
        "@id": `/variations/${esVariation.id}`,
        id: esVariation.id,
        translations: esVariation.name ? [{name: esVariation.name}] : [],
        sku: esVariation.sku ?? null,
        barcode: esVariation.barcode ?? null,
        externalId: esVariation.externalId ?? null,
        // variationEshopId is the variation's OWN eshop id — the document's
        // eshopId carries the parent product's.
        eshopId: esVariation.variationEshopId ?? null,
        pictures: esVariation.pictureUrl ? [esVariation.pictureUrl] : [],
        retailerOptionValues: (esVariation.options || []).map((option) => ({
            id: option.id,
            option: {
                id: option.optionId,
                translations: option.name ? [{name: option.name}] : [],
            },
            translations: option.value ? [{name: option.value}] : [],
        })),
        stock: esVariation.stockTotal ?? 0,
        // buyer demand tier (0 = no data)
        groupedSearchImpression: esVariation.groupedSearchImpression ?? 0,
        // all-time sales counters of the variation (docs/variation-sales.md)
        onlineSales: esVariation.onlineSales ?? null,
        storeSales: esVariation.storeSales ?? null,
        status: esVariation.status ?? false,
        validated: esVariation.validated ?? false,
        // {path, message} pairs explaining why validated is false — see
        // mapMerchandisingProduct for why this can be empty on an invalid variation.
        validationErrors: esVariation.validationErrors ?? EMPTY_VALIDATION_ERRORS,
        price: esVariation.price ?? null,
        outletPrice: esVariation.outletPrice ?? null,
        discountedPrice: esVariation.discountedPrice ?? null,
        // signed % vs the market benchmark (null = no scoring data)
        priceCompetitiveness: esVariation.priceCompetitiveness ?? null,
        updatedAt: esVariation.updatedAt ?? null,
        // Last edit made by a human, and its author (null when the variation has only
        // ever been written by imports). Same names as on the entity so the list reads
        // them identically whatever the source of the row.
        manualUpdatedAt: esVariation.manualUpdatedAt ?? null,
        manualUpdatedBy: esVariation.manualUpdatedBy ?? null,
        // Product url on the eshop, resolved in the organization's locale.
        url: esVariation.url ?? null,
        product: {
            id: esVariation.productId,
            "@id": `/products/${esVariation.productId}`,
            brand: {name: esVariation.brand ?? null},
            eikow: esVariation.eikow ?? false,
            variationFromFeed: esVariation.variationFromFeed ?? false,
            // The row reads product.currency.code (the real currency comes
            // from the organization); the ES document carries no currency.
            currency: {code: null},
            categories: (esVariation.directCategories || []).map((category) => ({
                id: category.id,
                translations: toTranslations(category.name),
                draft: category.draft ?? false,
            })),
        },
    };
}
